import React from "react";
import { ReactComponent as CalenderIcon } from "../../../../../assets/icons/svg/calender-icon.svg";
import { ReactComponent as DesktopPotrateIcon } from "../../../../../../assets/icons/svg/desktop-potrate-icon.svg";
import { ReactComponent as TagIcon } from "../../../../../../assets/icons/svg/tag-icon.svg";
import { ReactComponent as WifiLiveIcon } from "../../../../../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../../../../../assets/icons/svg/wifi-offline-icon.svg";
import { ReactComponent as Thunder } from "../../../../../../assets/icons/svg/thunder.svg";
import { ReactComponent as Clock } from "../../../../../../assets/icons/svg/clock.svg";
import { ReactComponent as SelectionIcon } from "../../../../../../assets/icons/svg/selection-icon.svg";
import ItemListPopup from "../../../../ui/ItemListPopup";
import AnnouncementTypeCard from "../../../../ui/AnnouncementTypeCard";
import ImageCard from "../../../../ui/DashbordActiveScreensImageCard";

interface CreativeWithDuration {
  id: string;
  title: string;
  imageUrl: string;
  fileFormat?: string;
  duration: number;
  orientation: "landscape" | "portrait";
  isPlaying: boolean;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  format: string;
  isScheduled?: boolean;
}

interface PlaylistScheduleDetails {
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleData: {
    selectedSlots?: Set<string> | string[];
    duration?: { value: string; label: string } | string;
    startDate?: Date | string;
    endDate?: Date | string;
  };
  announcementDetails?: {
    selectedTags?: string[];
    selectedLocations?: string[];
    totalCreatives?: number;
    creativeTitles?: string[];
    [key: string]: any;
  };
  scheduledAt?: string;
  dateRange?: string;
  locations?: string;
}

interface PlaylistDetailsPreviewProps {
  playlist: {
    id: string;
    title: string;
    imageUrl: string;
    status: "Playing" | "Draft Saved";
    isScheduled?: boolean;
    scheduledAt?: string;
    creativeIds: string[];
    creativeDurations: Record<string, number>;
    scheduleDetails?: PlaylistScheduleDetails;
    creativesCount: number; // Add this property
  };
  creatives: CreativeWithDuration[];
  scheduleDetails?: PlaylistScheduleDetails;
  onClose: () => void;
}

// Image Card Component with layered effect
const PlaylistImageCard: React.FC<{
  imageUrl: string;
  title: string;
  creativesCount: number;
  status: string;
}> = ({ imageUrl, title, creativesCount, status }) => {
  const [imageError, setImageError] = React.useState(false);

  return (
    <div className="w-full max-w-[280px] mx-auto">
      {/* Card Container with layered effect */}
      <div className="relative w-full aspect-[294/385]">
        {/* Stack Effect - Bottom Card (Darkest) */}
        <div
          className="absolute bg-[#606164]"
          style={{
            width: "84.35%",
            height: "100%",
            top: "5.19%",
            left: "7.82%",
            borderRadius: "min(28.42px, 7.2vw)",
            zIndex: 0,
          }}
        />
        
        {/* Stack Effect - Middle Card (Medium) */}
        <div
          className="absolute bg-[#46474A]"
          style={{
            width: "92.52%",
            height: "100%",
            top: "2.86%",
            left: "3.74%",
            borderRadius: "min(28.42px, 7.2vw)",
            zIndex: 1,
          }}
        />
        
        {/* Main Card */}
        <div
          className="w-full h-[99.48%] relative overflow-hidden z-10"
          style={{
            borderRadius: "min(28px, 7.1vw)",
          }}
        >
          {/* Image or Placeholder */}
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={title}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <svg
                className="w-16 h-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
          )}
          
          {/* Gradient Overlay for Text */}
          <div className="absolute inset-0 rounded-[28px] bg-gradient-to-t from-[rgba(27,29,33,0.7)] via-transparent to-transparent" />
          
          {/* Card Content */}
          <div className="absolute bottom-4 left-4 right-4 text-white z-10">
            {/* Playlist Title */}
            <h3 className="text-base 2xl:text-lg font-semibold leading-[1.5] mb-1">
              {title}
            </h3>
            
            {/* Metadata Row */}
            <div className="text-xs 2xl:text-sm flex items-center gap-1">
              {/* Creatives Count */}
              <span>
                {creativesCount === 0
                  ? "No content"
                  : `${creativesCount} creative${creativesCount !== 1 ? "s" : ""}`}
              </span>
              
              {/* Separator */}
              <span className="mx-1">|</span>
              
              {/* Status Display */}
              <span
                className={`flex items-center font-medium italic ${
                  status === "Playing"
                    ? "text-[#10B981]"
                    : "text-[#FF9500]"
                }`}
              >
                {status}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const PlaylistDetailsPreview: React.FC<PlaylistDetailsPreviewProps> = ({
  playlist,
  creatives,
  scheduleDetails,
  onClose,
}) => {
  // Debug logging to see what data we have
  React.useEffect(() => {
    console.log("=== PLAYLIST DETAILS PREVIEW DEBUG ===");
    console.log("Playlist:", playlist);
    console.log("Playlist.scheduleDetails:", playlist.scheduleDetails);
    console.log("Props.scheduleDetails:", scheduleDetails);
    console.log("Creatives:", creatives);
    console.log("Playlist status:", playlist.status);
    console.log("Playlist isScheduled:", playlist.isScheduled);
  }, [playlist, scheduleDetails, creatives]);

  // Use scheduleDetails from props or playlist
  const currentScheduleDetails = scheduleDetails || playlist.scheduleDetails;

  // Debug the final schedule details being used
  React.useEffect(() => {
    console.log("=== CURRENT SCHEDULE DETAILS ===");
    console.log("currentScheduleDetails:", currentScheduleDetails);
    if (currentScheduleDetails) {
      console.log("Schedule Type:", currentScheduleDetails.scheduleType);
      console.log("Announcement:", currentScheduleDetails.announcement);
      console.log("Schedule Data:", currentScheduleDetails.scheduleData);
      console.log(
        "Announcement Details:",
        currentScheduleDetails.announcementDetails
      );
    } else {
      console.log("No schedule details found!");
    }
  }, [currentScheduleDetails]);

  /**
   * Get dimensions display text (using first creative as reference)
   */
  const getDimensions = () => {
    if (creatives.length > 0) {
      const firstCreative = creatives[0];
      return firstCreative.orientation === "landscape"
        ? "1920 x 1080"
        : "1080 x 1920";
    }
    return "1920 x 1080"; // default
  };

  /**
   * Format date for display
   */
  const formatDate = (date: Date | string): string => {
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return typeof date === "string" ? date : "Invalid date";
    }
  };

  /**
   * Get schedule type display text with proper formatting
   */
  const getScheduleTypeText = () => {
    if (!currentScheduleDetails) return "Not scheduled";

    switch (currentScheduleDetails.scheduleType) {
      case "now":
        return "Now";
      case "later":
        return "Later";
      case "selection":
        return "Calendar selection";
      default:
        return "Scheduled";
    }
  };

  /**
   * Get schedule type display color
   */
  const getScheduleTypeColor = () => {
    if (!currentScheduleDetails) return "text-gray-500";

    switch (currentScheduleDetails.scheduleType) {
      case "now":
        return "text-[#0090AD]";
      case "later":
        return "text-[#0090AD]";
      case "selection":
        return "text-[#0090AD]";
      default:
        return "text-[#0090AD]";
    }
  };

  /**
   * Get duration display text from schedule details
   */
  const getDurationText = () => {
    if (!currentScheduleDetails?.scheduleData?.duration) return "Not specified";

    const duration = currentScheduleDetails.scheduleData.duration;
    if (typeof duration === "object" && duration !== null) {
      return duration.label || "10 sec";
    }
    return duration || "10 sec";
  };

  /**
   * Get total playlist duration
   */
  const getTotalPlaylistDuration = () => {
    const totalSeconds = creatives.reduce(
      (sum, creative) => sum + creative.duration,
      0
    );
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  /**
   * Get selected slots for custom schedule
   */
  const getSelectedSlots = (): Set<string> => {
    if (
      currentScheduleDetails?.scheduleType === "selection" &&
      currentScheduleDetails.scheduleData?.selectedSlots
    ) {
      if (Array.isArray(currentScheduleDetails.scheduleData.selectedSlots)) {
        return new Set(currentScheduleDetails.scheduleData.selectedSlots);
      }
      if (currentScheduleDetails.scheduleData.selectedSlots instanceof Set) {
        return currentScheduleDetails.scheduleData.selectedSlots;
      }
    }
    return new Set<string>();
  };

  /**
   * Get selected slots as array for processing
   */
  const getSelectedSlotsArray = (): string[] => {
    const selectedSlots = getSelectedSlots();
    return Array.from(selectedSlots);
  };

  /**
   * Get button class for calendar grid
   */
  const getButtonClass = (key: string) => {
    const slots = getSelectedSlots();
    const isSelected = slots.has(key);
    return isSelected
      ? "bg-[#CCF0FB] border border-white"
      : "bg-[#E4E4E4] opacity-30 border border-white";
  };

  /**
   * Get date range based on schedule type
   */
  const getDateRange = () => {
    if (!currentScheduleDetails) return "Not scheduled";

    // Use dateRange from schedule details if available
    if (currentScheduleDetails.dateRange) {
      return currentScheduleDetails.dateRange;
    }

    const scheduleData = currentScheduleDetails.scheduleData;

    if (currentScheduleDetails.scheduleType === "now" && scheduleData.endDate) {
      const startDate = formatDate(new Date());
      const endDate = formatDate(scheduleData.endDate);
      return `${startDate} - ${endDate}`;
    }

    if (
      currentScheduleDetails.scheduleType === "later" &&
      scheduleData.startDate &&
      scheduleData.endDate
    ) {
      const startDate = formatDate(scheduleData.startDate);
      const endDate = formatDate(scheduleData.endDate);
      return `${startDate} - ${endDate}`;
    }

    if (currentScheduleDetails.scheduleType === "selection") {
      return "Custom schedule selected";
    }

    return "Date range not specified";
  };

  /**
   * Get end date for "Now" schedule type
   */
  const getEndDate = () => {
    if (
      currentScheduleDetails?.scheduleType === "now" &&
      currentScheduleDetails.scheduleData?.endDate
    ) {
      return formatDate(currentScheduleDetails.scheduleData.endDate);
    }
    return null;
  };

  /**
   * Get selected tags count
   */
  const getSelectedTagsCount = () => {
    if (currentScheduleDetails?.announcementDetails?.selectedTags) {
      return currentScheduleDetails.announcementDetails.selectedTags.length;
    }
    // Fallback to creative tags if available
    const allTags = creatives.flatMap((creative) => creative.tags || []);
    const uniqueTags = Array.from(new Set(allTags));
    return uniqueTags.length;
  };

  /**
   * Get tags for display
   */
  const getTagsForDisplay = () => {
    if (currentScheduleDetails?.announcementDetails?.selectedTags) {
      return currentScheduleDetails.announcementDetails.selectedTags;
    }
    // Fallback to creative tags if available
    const allTags = creatives.flatMap((creative) => creative.tags || []);
    return Array.from(new Set(allTags));
  };

  /**
   * Get selected locations
   */
  const getSelectedLocations = () => {
    // First try to get from schedule details
    if (currentScheduleDetails?.announcementDetails?.selectedLocations) {
      return currentScheduleDetails.announcementDetails.selectedLocations;
    }

    // Use locations from schedule details if available
    if (currentScheduleDetails?.locations) {
      return currentScheduleDetails.locations
        .split(", ")
        .filter((loc) => loc.trim() !== "");
    }

    // Fallback to creative locations
    const locations = [];
    if (creatives && creatives.length > 0) {
      const locationsList = creatives.map((c) => c.locations).filter(Boolean);
      const uniqueLocations = Array.from(new Set(locationsList));
      locations.push(...uniqueLocations);
    }
    return locations.length > 0 ? locations : ["Not specified"];
  };

  // Get unique selected hours across all days and sort in ascending order
  const selectedSlotsArray = getSelectedSlotsArray();
  const selectedHours: string[] = [];

  if (selectedSlotsArray.length > 0) {
    const hoursArray: number[] = [];
    const seenHours = new Set<number>();

    // Extract unique hours without iterating over Set
    selectedSlotsArray.forEach((slot: string) => {
      const hour = parseInt(slot.split("-")[1], 10);
      if (!seenHours.has(hour)) {
        seenHours.add(hour);
        hoursArray.push(hour);
      }
    });

    const sortedHours = hoursArray
      .sort((a, b) => a - b)
      .map((hour) => String(hour).padStart(2, "0"));

    for (let i = 0; i < sortedHours.length; i++) {
      selectedHours.push(sortedHours[i]);
    }
  }

  const tagsToDisplay = getTagsForDisplay();
  const selectedLocations = getSelectedLocations();
  const endDate = getEndDate();

  // Debug what we're about to render
  const isScheduled = playlist.isScheduled || playlist.status === "Playing";
  const hasScheduleDetails = !!currentScheduleDetails;

  console.log("=== RENDER DEBUG ===");
  console.log("isScheduled:", isScheduled);
  console.log("hasScheduleDetails:", hasScheduleDetails);
  console.log("Will show schedule info:", isScheduled && hasScheduleDetails);

  // Get the playlist image URL - use first creative's image if available
  const getPlaylistImageUrl = () => {
    if (playlist.imageUrl) {
      return playlist.imageUrl;
    }
    // Fallback to first creative's image
    if (creatives && creatives.length > 0) {
      return creatives[0].imageUrl;
    }
    return '';
  };

  // Get creative count from playlist or creatives array
  const getCreativesCount = () => {
    return playlist.creativesCount || creatives.length;
  };

  return (
    <ItemListPopup title="Playlist details" onClose={onClose}>
      <div className="flex flex-col gap-6 h-full overflow-y-auto">
        {/* Playlist Header with Image and Info */}
        <div className="flex items-center gap-8 sm:gap-6">
          <ImageCard imageUrl={getPlaylistImageUrl()} />
          <div className="flex flex-col">
            <div className="flex items-center gap-6 sm:gap-3 flex-wrap">
              <h3 className="text-lg sm:text-base font-semibold">
                {playlist.title || "Untitled Playlist"}
              </h3>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 sm:gap-1 items-center">
              <span className="text-sm sm:text-xs text-gray-500 whitespace-nowrap">
                {getCreativesCount().toString().padStart(2, "0")} creatives
              </span>
              <span className="w-[1px] h-4 bg-gray-300"></span>
              <span className={`text-sm sm:text-xs font-medium italic ${
                playlist.status === "Playing" ? "text-[#10B981]" : "text-[#FF9500]"
              }`}>
                {playlist.status}
              </span>
            </div>
          </div>
        </div>

        

        {/* FIXED: Show schedule info when playlist is scheduled AND has schedule details */}
        {isScheduled && hasScheduleDetails && (
          <>
           
            {/* Announcement Type */}
            <div>
              <div className="text-sm font-medium text-[#1F1F1F] mb-2">
                Announcement type:
              </div>
              <AnnouncementTypeCard
                type={currentScheduleDetails.announcement}
                isSelected={true}
                showDescription={false}
              />
            </div>
             {/* Schedule Type and Duration */}
            <div className="relative w-full flex items-center justify-between">
              {/* Left side */}
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-[#1F1F1F]">
                  Schedule:
                </div>
              </div>

              {/* Right side */}
              <div
                className={`text-base font-semibold ${getScheduleTypeColor()}`}
              >
                {getScheduleTypeText()}
              </div>
            </div>

            {/* Custom Schedule Calendar (only for selection type) */}
            {currentScheduleDetails.scheduleType === "selection" &&
              selectedHours.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-[#1F1F1F] mb-2">
                    Selected day & time*:
                  </div>
                  <div className="bg-white text-black p-2 rounded-lg border">
                    <div className="grid grid-cols-[48px_repeat(7,1fr)] gap-1">
                      <div></div>
                      {days.map((day) => (
                        <div
                          key={day}
                          className="text-xs font-semibold text-center py-1"
                        >
                          {day}
                        </div>
                      ))}
                      {selectedHours.map((hour) => (
                        <React.Fragment key={hour}>
                          <div className="text-xs font-medium text-center py-1">
                            {hour}
                          </div>
                          {days.map((day) => {
                            const key = `${day}-${hour}`;
                            return (
                              <div
                                key={key}
                                className={`w-full h-[22px] rounded-[4px] cursor-default ${getButtonClass(
                                  key
                                )}`}
                              />
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            {/* Start & End Date */}
            <div>
              {/* Show end date for "Now" schedule */}
              {currentScheduleDetails.scheduleType === "now" && endDate && (
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1F1F1F]">
                    End date:
                  </div>
                  <div className="text-base font-semibold text-[#0090AD]">
                    {endDate}
                  </div>
                </div>
              )}

              {/* Show start & end date for other schedule types */}
              {currentScheduleDetails.scheduleType !== "selection" && (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-sm font-medium text-[#1F1F1F]">
                    {currentScheduleDetails.scheduleType === "now"
                      ? "Date range:"
                      : "Start & end date:"}
                  </div>
                  <div className="text-base font-semibold text-[#0090AD]">
                    {getDateRange()}
                  </div>
                </div>
              )}
            </div>

            {/* Selected Tags (for tag-level announcement) */}
            {currentScheduleDetails.announcement === "tag-level" && (
              <div>
                <div className="text-sm font-medium text-[#1F1F1F] mb-2">
                  Selected ({getSelectedTagsCount()})
                </div>
                <div className="flex flex-wrap gap-2">
                  {tagsToDisplay.length > 0 ? (
                    tagsToDisplay
                      .slice(0, 6)
                      .map((tag: string, index: number) => (
                        <div
                          key={index}
                          className="px-3 py-1.5 bg-gray-100 text-[#1F1F1F] rounded-[10px] text-sm font-medium border border-[#000000]"
                        >
                          {tag}
                        </div>
                      ))
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      No tags selected
                    </div>
                  )}
                  {tagsToDisplay.length > 6 && (
                    <div className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium border">
                      +{tagsToDisplay.length - 6} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ItemListPopup>
  );
};

export default PlaylistDetailsPreview;